import {
  ChartSettingsEntry,
  DataPrefix,
  DataSuffix,
  NumberFormatSwitch,
} from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { CSSProperties, ChangeEvent, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_NUMBER_FORMAT } from 'widgets/ChartWidgets/ChartWidget.config';
import { ChartWidgetData, NumberFormatSettingsType } from 'widgets/ChartWidgets/ChartWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { useWidget } from 'widgets/sdk';

import { AxisPropertyName } from '../ChartSettingsAxis/ChartSettingsAxis.types';
import {
  BUTTON_OPTION_STYLE,
  DEFAULT_DECIMAL,
  DEFAULT_SEPARATOR,
  HAS_PERCENTAGE,
  TRIGGER_STYLE,
  decimalConfig,
  separatorConfig,
} from './NumberFormat.config';

interface DataLabelAdornmentProps {
  axisName?: AxisPropertyName;
}

export const DataLabelAdornment = memo(({ axisName = 'yAxis' }: DataLabelAdornmentProps) => {
  const {
    updateWidget,
    dataLabels: { prefix: prefixProp, suffix: suffixProp, y2Prefix: y2PrefixProp, y2Suffix: y2SuffixProp },
    numberFormat: { format = DEFAULT_NUMBER_FORMAT } = {},
    widgetId,
  } = useWidget<ChartWidgetData>();

  const currentPrefix = (axisName === 'y2Axis' ? y2PrefixProp : prefixProp) ?? '';
  const currentSuffix = (axisName === 'y2Axis' ? y2SuffixProp : suffixProp) ?? '';

  // Because the prefix and the suffix are from the proxy object,
  // we need to use state to store the value and to fix the issue
  // that the cursor would move to the end of the input field when editing between text.
  const [prefix, setPrefix] = useState<string>(currentPrefix);
  const [suffix, setSuffix] = useState<string>(currentSuffix);

  // Work around of the undo/redo issue
  useEffect(() => {
    setPrefix(currentPrefix);
    setSuffix(currentSuffix);
  }, [currentPrefix, currentSuffix, setPrefix, setSuffix]);

  const hasPercentageOption = HAS_PERCENTAGE.includes(getWidgetTypeFromId(widgetId));
  const showValueModification = format === 'value' || (format === 'percentage' && !hasPercentageOption);
  if (!showValueModification) return null;

  const onPrefixChange = (event: ChangeEvent<HTMLInputElement>) => {
    const prefixValue = event.target.value;

    setPrefix(prefixValue);
    if (axisName === 'y2Axis') {
      updateWidget({ dataLabels: { y2Prefix: prefixValue } });
    } else {
      updateWidget({ dataLabels: { prefix: prefixValue } });
    }
  };

  const onSuffixChange = (event: ChangeEvent<HTMLInputElement>) => {
    const suffixValue = event.target.value;

    setSuffix(suffixValue);
    if (axisName === 'y2Axis') {
      updateWidget({ dataLabels: { y2Suffix: suffixValue } });
    } else {
      updateWidget({ dataLabels: { suffix: suffixValue } });
    }
  };

  return (
    <>
      <DataPrefix value={prefix} onChange={onPrefixChange} />
      <DataSuffix value={suffix} onChange={onSuffixChange} />
    </>
  );
});
DataLabelAdornment.displayName = 'DataLabelAdornment';

interface DataLabelFormatProps {
  axisName?: AxisPropertyName;
}

export const DataLabelFormat = memo(({ axisName = 'yAxis' }: DataLabelFormatProps) => {
  const { updateWidget, numberFormat: { y2Precision = DEFAULT_DECIMAL, y2Separator = DEFAULT_SEPARATOR } = {} } =
    useWidget<ChartWidgetData>();

  const precision = axisName === 'y2Axis' ? y2Precision : undefined;
  const separator = axisName === 'y2Axis' ? y2Separator : undefined;

  const onPrecisionChange =
    axisName === 'y2Axis'
      ? (value: string) => {
          updateWidget({ numberFormat: { y2Precision: value } });
        }
      : undefined;

  const onSeparatorChange =
    axisName === 'y2Axis'
      ? (value: string) => {
          updateWidget({ numberFormat: { y2Separator: value } });
        }
      : undefined;

  return (
    <>
      <Separator separator={separator} onSeparatorChange={onSeparatorChange} />
      <Precision precision={precision} onPrecisionChange={onPrecisionChange} />
    </>
  );
});
DataLabelFormat.displayName = 'DataLabelFormat';

export const Separator = memo(
  ({
    separator: separatorProp,
    onSeparatorChange: onSeparatorChangeProp,
  }: {
    separator?: string;
    onSeparatorChange?: (_: string) => void;
  }) => {
    const { t } = useTranslation('chart_widget', { useSuspense: false });
    const label = t('separator.label');

    const translatedConfig = useMemo(() => {
      return separatorConfig.map((option) => ({
        ...option,
        label: t(`separator.options.${option.label}` as keyof typeof t),
      }));
    }, [t]);

    const { updateWidget, numberFormat: { separator = DEFAULT_SEPARATOR } = {} } = useWidget<ChartWidgetData>();
    const currentSeparator = separatorProp ?? separator;

    const selectedIndex = separatorConfig.findIndex((option) => option.value === currentSeparator);

    const onSelect = (value: string) => {
      onSeparatorChangeProp ? onSeparatorChangeProp(value) : updateWidget({ numberFormat: { separator: value } });
    };

    return (
      <ChartSettingsEntry label={label}>
        <DropdownPopover
          closeOnSelect
          returnFocusOnClose={false}
          label={currentSeparator}
          onSelect={onSelect}
          options={translatedConfig}
          spacing={1}
          selectedIndex={selectedIndex}
          w={'7rem'}
          triggerProps={TRIGGER_STYLE}
          buttonOptionProps={BUTTON_OPTION_STYLE}
        />
      </ChartSettingsEntry>
    );
  },
);
Separator.displayName = 'Separator';

export const Precision = memo(
  ({
    precision: precisionProp,
    onPrecisionChange,
  }: {
    precision?: string;
    onPrecisionChange?: (_: string) => void;
  }) => {
    const { t } = useTranslation('chart_widget', { useSuspense: false });
    const label = t('precision.label');

    const translatedConfig = useMemo(() => {
      return decimalConfig.map((option) => ({
        ...option,
        label: t(`precision.options.${option.label}` as keyof typeof t),
      }));
    }, [t]);

    const { updateWidget, numberFormat: { precision = DEFAULT_DECIMAL } = {} } = useWidget<ChartWidgetData>();
    const currentPrecision = precisionProp ?? precision;

    const onSelect = (value: string) => {
      onPrecisionChange ? onPrecisionChange(value) : updateWidget({ numberFormat: { precision: value } });
    };

    return (
      <ChartSettingsEntry label={label}>
        <DropdownPopover
          closeOnSelect
          returnFocusOnClose={false}
          label={currentPrecision}
          onSelect={onSelect}
          options={translatedConfig}
          spacing={1}
          w={'7rem'}
          triggerProps={{
            height: 7,
            marginY: '6px',
            fontSize: 'sm',
            fontWeight: 'normal',
            sx: {
              '& > p': {
                fontSize: 'sm',
                fontWeight: 'normal',
              },
            },
          }}
          buttonOptionProps={{
            height: 7,
            lineHeight: 5,
            fontWeight: 'normal',
          }}
        />
      </ChartSettingsEntry>
    );
  },
);
Precision.displayName = 'Precision';

export const DataNumberFormat = ({ style }: { style?: CSSProperties }) => {
  const { numberFormat: { format = DEFAULT_NUMBER_FORMAT } = {}, updateWidget } = useWidget<ChartWidgetData>();

  const onChangeNumberFormat = (format: NonNullable<NumberFormatSettingsType['format']>) => {
    updateWidget({ numberFormat: { format } });
  };

  return <NumberFormatSwitch onChange={onChangeNumberFormat} value={format} style={style} />;
};
